<script lang="ts" setup>
import { SancareFindBar } from '@sancare/typescript-commons'
import { ComponentSize, FindBarColor } from '@sancare/typescript-commons/src/components/types'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import _ from 'lodash'
import { computed, ComputedRef, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { stay } from '@/stay-displayer/types'
import { useStore } from '@/store'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const { t } = useI18n()

const emit = defineEmits<{
  (e: 'handle-submit', payload: { result?: stay, input: string }): void
  (e: 'handle-click', payload: { result: stay, input: string }): void
}>()

const props = withDefaults(defineProps<{
  color?: FindBarColor
  defaultSearchValue?: string
  fullWidth?: boolean
  size?: ComponentSize
}>(), {
  color: FindBarColor.BLACK,
  defaultSearchValue: '',
  fullWidth: true,
  size: ComponentSize.MEDIUM,
})

const searchInput = ref('')

const pmsiType: ComputedRef<PmsiTypeEnum> = computed(() => store.getters.pmsiType)
const searchResults = computed(() => store.state.stayDisplayer.searchStayResults)

onMounted(() => {
  if (!props.defaultSearchValue) {
    return
  }

  searchInput.value = props.defaultSearchValue
})

const throttleSearch = _.throttle(async function(newSearchInput: string) {
  if (!newSearchInput) {
    store.commit('clearSearchStay')
    return
  }

  await store.dispatch('searchStay', { search: newSearchInput, pmsiType: pmsiType.value })
}, 500, { leading: false, maxWait: 500, trailing: true })

function handleSubmit() {
  emit('handle-submit', { ...getFinalResult() })
}

function handleClick(stay: stay) {
  emit('handle-click', { ...getFinalResult(stay) })
}

function getFinalResult(stay?: stay) {
  const firstResult = searchResults.value.length ? searchResults.value[0] : {}
  const result = _.isEmpty(stay) ? firstResult : stay

  return {
    result: result,
    input: searchInput.value,
  }
}

function dateFormat(date, frmt) {
  return dayjs(date).format(frmt)
}

watch(searchInput, throttleSearch)

watch(pmsiType, () => {
  store.commit('clearSearchStay')

  if (searchInput.value) {
    throttleSearch(searchInput.value)
  }
})
</script>

<template>
  <sancare-find-bar
    v-model="searchInput"
    :placeholder="t('header.findBar.search')"
    :results="searchResults"
    :fit-placeholder="!fullWidth"
    :size="size"
    :color="color"
    @submit="handleSubmit"
    @select="handleClick"
  >
    <template #result="{ item }">
      <div class="stay-search-list-title">
        <template v-if="pmsiType === PmsiTypeEnum.MCO">
          <span v-if="item.rssId">{{ t('rssId') }} {{ item.rssId }} – </span>
        </template>
        <template v-else>
          <span v-if="item.smrId">{{ t('smrId') }} {{ item.smrId }} – </span>
        </template>
        <span v-if="item.administrativeLocalStayId">
          {{ t('administrativeId') }} {{ item.administrativeLocalStayId }}
        </span>
      </div>
      <div class="stay-search-list-caption row">
        <div class="col">
          {{ item.patient.firstName }}
          {{ item.patient.lastName || item.patient.maidenName }}
        </div>
        <div class="col-auto">
          {{ item.stayDuration }}J
        </div>
        <div class="col-auto">
          {{ capitalize(t('time.on')) }} {{ dateFormat(item.stayStart, 'L') }}
        </div>
      </div>
    </template>
  </sancare-find-bar>
</template>
